import React, { useState, useEffect } from 'react';
import { bannersApi } from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Info, 
  AlertTriangle, 
  AlertCircle, 
  CheckCircle, 
  Sparkles,
  X,
  ChevronLeft,
  ChevronRight,
  ExternalLink
} from 'lucide-react';

const BannerDisplay = ({ maxBanners = 50, className = '' }) => {
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [dismissedBanners, setDismissedBanners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const res = await bannersApi.getActive(maxBanners);
        setBanners(res.data);
      } catch (error) {
        console.error('Error fetching banners:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBanners();
    
    // Refresh banners every 5 minutes
    const interval = setInterval(fetchBanners, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [maxBanners]);

  const visibleBanners = banners.filter(b => !dismissedBanners.includes(b.banner_id));

  // Auto-rotate banners every 8 seconds
  useEffect(() => {
    if (visibleBanners.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % visibleBanners.length);
    }, 8000);
    
    return () => clearInterval(interval);
  }, [visibleBanners.length]);

  if (loading || visibleBanners.length === 0) return null;

  const currentBanner = visibleBanners[currentIndex] || visibleBanners[0];

  const handleDismiss = (bannerId) => {
    setDismissedBanners(prev => [...prev, bannerId]);
    if (currentIndex >= visibleBanners.length - 1) {
      setCurrentIndex(0);
    }
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + visibleBanners.length) % visibleBanners.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % visibleBanners.length);
  };

  const getTypeStyles = (type) => {
    switch (type) {
      case 'info':
        return {
          bg: 'bg-[#2979FF]/10 border-[#2979FF]/30',
          icon: <Info size={20} className="text-[#2979FF]" />,
          accent: 'text-[#2979FF]'
        };
      case 'warning':
        return {
          bg: 'bg-[#FFEA00]/10 border-[#FFEA00]/30',
          icon: <AlertTriangle size={20} className="text-[#FFEA00]" />,
          accent: 'text-[#FFEA00]'
        };
      case 'urgent':
        return {
          bg: 'bg-[#FF1744]/10 border-[#FF1744]/30',
          icon: <AlertCircle size={20} className="text-[#FF1744]" />,
          accent: 'text-[#FF1744]'
        };
      case 'success':
        return {
          bg: 'bg-[#00E676]/10 border-[#00E676]/30',
          icon: <CheckCircle size={20} className="text-[#00E676]" />,
          accent: 'text-[#00E676]'
        };
      case 'promo':
        return {
          bg: 'bg-purple-500/10 border-purple-500/30',
          icon: <Sparkles size={20} className="text-purple-400" />,
          accent: 'text-purple-400'
        };
      default:
        return {
          bg: 'bg-[#2979FF]/10 border-[#2979FF]/30',
          icon: <Info size={20} className="text-[#2979FF]" />,
          accent: 'text-[#2979FF]'
        };
    }
  };

  const styles = getTypeStyles(currentBanner.banner_type);

  const BannerContent = () => {
    // If HTML content is present, render it directly
    if (currentBanner.html_content) {
      return (
        <div className="flex-1 min-w-0">
          <div dangerouslySetInnerHTML={{ __html: currentBanner.html_content }} />
        </div>
      );
    }

    return (
      <div className="flex items-start gap-4 flex-1">
        <div className="flex-shrink-0 mt-0.5">
          {styles.icon}
        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className={`font-bold text-sm uppercase tracking-wider ${styles.accent}`}>
            {currentBanner.title}
          </h4>
          <p className="text-slate-300 text-sm mt-1">
            {currentBanner.message}
          </p>
        </div>

        {currentBanner.image_url && (
          <div className="flex-shrink-0 hidden sm:block">
            <img 
              src={currentBanner.image_url} 
              alt="" 
              className="w-16 h-16 object-cover rounded border border-white/10"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`relative ${className}`}>
      <AnimatePresence mode="wait">
        <motion.div
          key={currentBanner.banner_id}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          transition={{ duration: 0.3 }}
          className={`${styles.bg} border rounded-sm p-4 relative`}
          data-testid={`banner-${currentBanner.banner_id}`}
        >
          {currentBanner.link ? (
            <a 
              href={currentBanner.link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start gap-4 group"
              data-testid="banner-link"
            >
              <BannerContent />
              <ExternalLink size={16} className="text-slate-400 group-hover:text-white transition-colors flex-shrink-0 mt-1" />
            </a>
          ) : (
            <BannerContent />
          )}

          {/* Dismiss button */}
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleDismiss(currentBanner.banner_id);
            }}
            className="absolute top-2 right-2 p-1 text-slate-500 hover:text-white transition-colors"
            data-testid="banner-dismiss"
          >
            <X size={16} />
          </button>

          {/* Navigation dots and arrows */}
          {visibleBanners.length > 1 && (
            <div className="flex items-center justify-center gap-2 mt-3 pt-2 border-t border-white/10">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handlePrev();
                }}
                className="p-1 text-slate-500 hover:text-white transition-colors"
                data-testid="banner-prev"
              >
                <ChevronLeft size={16} />
              </button>
              
              <div className="flex items-center gap-1.5">
                {visibleBanners.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={(e) => {
                      e.preventDefault();
                      setCurrentIndex(idx);
                    }}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      idx === currentIndex ? styles.accent.replace('text-', 'bg-') : 'bg-slate-600'
                    }`}
                    data-testid={`banner-dot-${idx}`}
                  />
                ))}
              </div>
              
              <button
                onClick={(e) => {
                  e.preventDefault();
                  handleNext();
                }}
                className="p-1 text-slate-500 hover:text-white transition-colors"
                data-testid="banner-next"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default BannerDisplay;
